import { useState, useMemo, useEffect } from 'react';
import { Property } from '../mock-data';
import { BarChart3, ChevronUp, ChevronDown } from 'lucide-react';
import { getEfficiencyColor, getEfficiencyCategory } from '../utils';
import { Tooltip } from 'react-tooltip';
import type { TimePeriod } from '../types';
import { formatPeriodDisplay } from '../types';

// Region mapping and abbreviations
const PROVINCE_TO_REGION: Record<string, string> = {
  'Bangkok': 'C',
  'Pathum Thani': 'C',
  'Nonthaburi': 'C',
  'Ayutthaya': 'C',
  'Saraburi': 'C',
  'Nakhon Pathom': 'C',
  'Chiang Mai': 'N',
  'Chiang Rai': 'N',
  'Lampang': 'N',
  'Khon Kaen': 'NE',
  'Udon Thani': 'NE',
  'Nakhon Ratchasima': 'NE',
  'Ubon Ratchathani': 'NE',
  'Chonburi': 'E',
  'Rayong': 'E',
  'Phuket': 'S',
  'Songkhla': 'S',
  'Surat Thani': 'S',
  'Nakhon Si Thammarat': 'S',
  'Krabi': 'S',
  'Trang': 'S',
  'Nakhon Sawan': 'C',
  'Samut Prakan': 'C',
};

interface UsageComparisonProps {
  selectedSites: string[];
  sites: Property[];
  isDemoMode: boolean;
  selectedPeriod: TimePeriod;
  aggregatedData?: any[];
  isLoadingAggregatedData?: boolean;
  aggregatedMetrics?: {
    totalEnergy: number;
    totalSolarEnergy: number;
    totalGridEnergy: number;
    totalOccupancy: number;
    totalInCount: number;
    totalOutCount: number;
    averageEUI: number;
    energySavingSaved: number;
    totalEnergySavingSaved: number;
    carbonEmissionSaved: number;
    equivalentTreesPlanted: number;
    avgOutdoorTemp: number;
    avgIndoorTemp: number;
    selectedSitesCount: number;
    totalArea: number;
    perSiteMetrics?: any[];
  } | null;
}

type SortField = 'branch' | 'eui' | 'indoorTemp' | 'outdoorTemp' | 'solar' | 'grid' | 'totalConsumption';
type SortDirection = 'asc' | 'desc';

export function UsageComparison({ selectedSites, sites, isDemoMode, selectedPeriod, aggregatedData, isLoadingAggregatedData, aggregatedMetrics }: UsageComparisonProps) {
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  
  // Generate site data based on demo mode and selected period
  const siteData = useMemo(() => {
    // Use per-site metrics from aggregatedMetrics if available
    if (!isDemoMode && aggregatedMetrics?.perSiteMetrics) {
      return sites.map(site => {
        const siteMetrics = aggregatedMetrics.perSiteMetrics?.find(m => m.siteId === site.id);
        
        if (siteMetrics && site.isOnline) {
          const totalEnergy = siteMetrics.energy.totalEnergy || 1; // Avoid division by zero
          const solarPercent = totalEnergy > 0 ? Math.round((siteMetrics.energy.solarEnergy / totalEnergy) * 100) : 0;
          
          return {
            ...site,
            indoorTemp: Math.round(siteMetrics.temperature.avgIndoor) || 25,
            outdoorTemp: Math.round(siteMetrics.temperature.avgOutdoor) || 30,
            solarPercent,
            gridPercent: 100 - solarPercent,
            totalConsumption: Math.round(siteMetrics.energy.totalEnergy),
            efficiency: Math.round(siteMetrics.energy.eui) || site.efficiency || 0
          };
        }
        
        // Fallback for sites without metrics or offline sites
        return {
          ...site,
          indoorTemp: site.isOnline ? 25 : 0,
          outdoorTemp: site.isOnline ? 30 : 0,
          solarPercent: 0,
          gridPercent: 100,
          totalConsumption: 0,
          efficiency: site.efficiency || 0
        };
      });
    }
    
    if (isDemoMode) {
      // Demo mode: use mock/random data
      const getIndoorTemp = () => Math.floor(Math.random() * 5) + 22; // 22-27°C
      const getOutdoorTemp = () => Math.floor(Math.random() * 10) + 28; // 28-38°C
      const getSolarPercentage = () => Math.floor(Math.random() * 30) + 10; // 10-40%
      
      // Adjust consumption based on period
      const getTotalConsumption = () => {
        const baseConsumption = Math.floor(Math.random() * 50000) + 10000;
        switch (selectedPeriod) {
          case 'this-month':
            return Math.floor(baseConsumption * 0.3); // Monthly data is smaller
          case 'this-year':
            return Math.floor(baseConsumption * 3); // Yearly data is larger
          case 'all-time':
            return Math.floor(baseConsumption * 5); // All-time data is largest
          default:
            return baseConsumption;
        }
      };
      
      return sites.map(site => {
        const solarPercent = getSolarPercentage();
        return {
          ...site,
          indoorTemp: getIndoorTemp(),
          outdoorTemp: getOutdoorTemp(),
          solarPercent,
          gridPercent: 100 - solarPercent,
          totalConsumption: getTotalConsumption()
        };
      });
    } else if (aggregatedData && aggregatedData.length > 0) {
      // Production mode with real data
      return sites.map(site => {
        const siteData = aggregatedData.find(d => d?.siteId === site.id);
        
        if (siteData) {
          const totalEnergy = siteData.energy.totalEnergy || 1; // Avoid division by zero
          const solarPercent = totalEnergy > 0 ? Math.round((siteData.energy.solarEnergy / totalEnergy) * 100) : 0;
          
          return {
            ...site,
            indoorTemp: Math.round(siteData.temperature.avgIndoor) || 25,
            outdoorTemp: Math.round(siteData.temperature.avgOutdoor) || 30,
            solarPercent,
            gridPercent: 100 - solarPercent,
            totalConsumption: Math.round(siteData.energy.totalEnergy)
          };
        }
        
        // Fallback for sites without data (offline sites should not have data)
        return {
          ...site,
          indoorTemp: site.isOnline ? 25 : 0,
          outdoorTemp: site.isOnline ? 30 : 0,
          solarPercent: 0,
          gridPercent: 100,
          totalConsumption: 0
        };
      });
    } else {
      // No data available, return empty metrics
      return sites.map(site => ({
        ...site,
        indoorTemp: 25,
        outdoorTemp: 30,
        solarPercent: 0,
        gridPercent: 100,
        totalConsumption: 0
      }));
    }
  }, [sites, isDemoMode, selectedPeriod, aggregatedData, aggregatedMetrics]);

  // Sort the data
  const sortedSites = useMemo(() => {
    // Default sort by online status first, then by branch code in ascending order
    if (!sortField) {
      return [...siteData].sort((a, b) => {
        // First sort by online status (online sites first)
        if (a.isOnline !== b.isOnline) {
          return a.isOnline ? -1 : 1;
        }
        // Then sort by branch code
        const aCode = a.code.toUpperCase();
        const bCode = b.code.toUpperCase();
        if (aCode < bCode) return -1;
        if (aCode > bCode) return 1;
        return 0;
      });
    }
    
    return [...siteData].sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortField) {
        case 'branch':
          aValue = a.code;
          bValue = b.code;
          break;
        case 'eui':
          aValue = a.efficiency;
          bValue = b.efficiency;
          break;
        case 'indoorTemp':
          aValue = a.indoorTemp;
          bValue = b.indoorTemp;
          break;
        case 'outdoorTemp':
          aValue = a.outdoorTemp;
          bValue = b.outdoorTemp;
          break;
        case 'solar':
          aValue = a.solarPercent;
          bValue = b.solarPercent;
          break;
        case 'grid':
          aValue = a.gridPercent;
          bValue = b.gridPercent;
          break;
        case 'totalConsumption':
          aValue = a.totalConsumption;
          bValue = b.totalConsumption;
          break;
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [siteData, sortField, sortDirection]);
  
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-light-grey p-3 pt-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <div className="w-[18px] h-[18px] bg-light-grey rounded-[4px] flex items-center justify-center">
            <BarChart3 className="w-[14px] h-[14px] text-dark-grey" />
          </div>
          <div className="flex items-center gap-2">
            <h3 className="heading-7 text-dark-blue">Usage Comparison</h3>
            <span className="text-[9px] font-medium text-dark-grey bg-light-grey px-2 py-0.5 rounded">
              {formatPeriodDisplay(selectedPeriod)}
            </span>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
        <table className="w-full">
          <thead className="sticky top-0 bg-white z-10">
            <tr className="border-b border-light-grey">
              <th 
                className="text-left py-2 px-2 text-[10px] font-semibold text-dark-grey bg-white cursor-pointer hover:bg-gray-50"
                onClick={() => handleSort('branch')}
              >
                <div className="flex items-center gap-1">
                  Branch
                  {sortField === 'branch' && (
                    sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                  )}
                </div>
              </th>
              <th 
                className="text-center py-2 px-2 text-[10px] font-semibold text-dark-grey bg-white cursor-pointer hover:bg-gray-50"
                onClick={() => handleSort('eui')}
              >
                <div className="flex items-center justify-center gap-1">
                  EUI (kWh/m²)
                  {sortField === 'eui' && (
                    sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                  )}
                </div>
              </th>
              <th 
                className="text-center py-2 px-2 text-[10px] font-semibold text-dark-grey bg-white cursor-pointer hover:bg-gray-50"
                onClick={() => handleSort('indoorTemp')}
              >
                <div className="flex items-center justify-center gap-1">
                  Indoor Temp
                  {sortField === 'indoorTemp' && (
                    sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                  )}
                </div>
              </th>
              <th 
                className="text-center py-2 px-2 text-[10px] font-semibold text-dark-grey bg-white cursor-pointer hover:bg-gray-50"
                onClick={() => handleSort('outdoorTemp')}
              >
                <div className="flex items-center justify-center gap-1">
                  Outdoor Temp
                  {sortField === 'outdoorTemp' && (
                    sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                  )}
                </div>
              </th>
              <th 
                className="text-center py-2 px-2 text-[10px] font-semibold text-dark-grey bg-white cursor-pointer hover:bg-gray-50"
                onClick={() => handleSort('solar')}
              >
                <div className="flex items-center justify-center gap-1">
                  Solar (%)
                  {sortField === 'solar' && (
                    sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                  )}
                </div>
              </th>
              <th 
                className="text-center py-2 px-2 text-[10px] font-semibold text-dark-grey bg-white cursor-pointer hover:bg-gray-50"
                onClick={() => handleSort('grid')}
              >
                <div className="flex items-center justify-center gap-1">
                  Grid (%)
                  {sortField === 'grid' && (
                    sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                  )}
                </div>
              </th>
              <th 
                className="text-right py-2 px-2 text-[10px] font-semibold text-dark-grey bg-white cursor-pointer hover:bg-gray-50"
                onClick={() => handleSort('totalConsumption')}
              >
                <div className="flex items-center justify-end gap-1">
                  Total Consumption
                  {sortField === 'totalConsumption' && (
                    sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                  )}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>

            {sortedSites.map((site) => {
              
              return (
                <tr 
                  key={site.id} 
                  className={`border-b border-light-grey transition-colors cursor-pointer ${
                    !site.isOnline 
                      ? 'bg-gray-100 text-gray-500 hover:bg-gray-200' 
                      : 'hover:bg-gray-50'
                  }`}
                  data-tooltip-id={`site-tooltip-${site.id}`}
                  data-tooltip-place="right"
                  data-tooltip-offset={5}
                >
                  <td className="py-2 px-2">
                    <div className="flex items-center gap-1">
                      <span className="text-[11px] font-medium text-black truncate">
                        {site.code}
                      </span>
                      <span className="text-[8px] text-gray-400 font-medium bg-gray-100 px-1 rounded">
                        {PROVINCE_TO_REGION[site.province] || 'N/A'}
                      </span>
                    </div>
                  </td>
                  <td className="text-center py-2 px-2">
                    <div className="flex items-center justify-center gap-1">
                      {isLoadingAggregatedData && !isDemoMode && site.isOnline ? (
                        <div className="h-4 w-16 bg-gray-200 animate-pulse rounded"></div>
                      ) : (
                        <>
                          <span className="text-[11px] font-semibold text-black">
                            {!site.isOnline ? '-' : site.efficiency}
                          </span>
                          {site.isOnline && (
                            <span 
                              className="text-[9px] font-medium px-1 rounded"
                              style={{ 
                                color: getEfficiencyColor(site.efficiency || 0)
                              }}
                            >
                              {getEfficiencyCategory(site.efficiency || 0)}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                  <td className="text-center py-2 px-2">
                    {isLoadingAggregatedData && !isDemoMode && site.isOnline ? (
                      <div className="h-4 w-12 bg-gray-200 animate-pulse rounded mx-auto"></div>
                    ) : (
                      <span className="text-[11px] font-medium text-black">
                        {!site.isOnline ? '-' : `${site.indoorTemp}°C`}
                      </span>
                    )}
                  </td>
                  <td className="text-center py-2 px-2">
                    {isLoadingAggregatedData && !isDemoMode && site.isOnline ? (
                      <div className="h-4 w-12 bg-gray-200 animate-pulse rounded mx-auto"></div>
                    ) : (
                      <span className="text-[11px] font-medium text-black">
                        {!site.isOnline ? '-' : `${site.outdoorTemp}°C`}
                      </span>
                    )}
                  </td>
                  <td className="text-center py-2 px-2">
                    {isLoadingAggregatedData && !isDemoMode && site.isOnline ? (
                      <div className="h-4 w-10 bg-gray-200 animate-pulse rounded mx-auto"></div>
                    ) : (
                      <span className="text-[11px] font-medium" style={{ color: '#F2AA32' }}>
                        {!site.isOnline ? '-' : `${site.solarPercent}%`}
                      </span>
                    )}
                  </td>
                  <td className="text-center py-2 px-2">
                    {isLoadingAggregatedData && !isDemoMode && site.isOnline ? (
                      <div className="h-4 w-10 bg-gray-200 animate-pulse rounded mx-auto"></div>
                    ) : (
                      <span className="text-[11px] font-medium" style={{ color: '#7D3BBF' }}>
                        {!site.isOnline ? '-' : `${site.gridPercent}%`}
                      </span>
                    )}
                  </td>
                  <td className="text-right py-2 px-2">
                    <div className="flex items-center justify-end gap-1">
                      {isLoadingAggregatedData && !isDemoMode && site.isOnline ? (
                        <div className="h-4 w-16 bg-gray-200 animate-pulse rounded"></div>
                      ) : (
                        <>
                          <span className="text-[11px] font-semibold text-black">
                            {!site.isOnline ? '-' : site.totalConsumption.toLocaleString()}
                          </span>
                          {site.isOnline && <span className="text-[9px] text-gray-500">kWh</span>}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {/* Tooltips for each site */}
      {sortedSites.map((site) => (
        <Tooltip
          key={`tooltip-${site.id}`}
          id={`site-tooltip-${site.id}`}
          opacity={1}
          style={{
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            padding: '10px 14px',
            borderRadius: '8px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            maxWidth: '280px',
            zIndex: 9999,
            fontSize: '12px'
          }}
        >
          <div className="space-y-2">
            <div className="font-semibold text-white text-sm border-b border-white/20 pb-1">{site.name} ({site.code})</div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-200">
              <div><span className="text-blue-300">Province:</span> {site.province}</div>
              <div><span className="text-blue-300">Region:</span> {site.region}</div>
              <div><span className="text-orange-300">EUI:</span> {site.efficiency} kWh/m²·yr</div>
              <div><span className="text-green-300">Status:</span> 
                <span className={`ml-1 px-1.5 py-0.5 rounded text-xs ${
                  !site.isOnline ? 'bg-red-500 text-white' : 
                  site.hasAlarm ? 'bg-yellow-500 text-black' : 
                  'bg-green text-white'
                }`}>
                  {!site.isOnline ? 'Offline' : site.hasAlarm ? 'Alarm' : 'Online'}
                </span>
              </div>
              {/* <div><span className="text-purple-300">Updated:</span> {site.lastUpdated}</div> */}
            </div>
          </div>
        </Tooltip>
      ))}
    </div>
  );
}